import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Field } from "@/components/Field";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ReportLog = ({ items }) => (
  <ul className="mt-6 divide-y rounded-md border" data-testid="report-delivery-log">
    {!items.length && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No reports sent yet.</li>}
    {items.map((d, i) => (
      <li key={i} className="px-4 py-3 text-sm" data-testid={`report-log-${i}`}>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={d.status === "Partial" ? "Failed" : d.status} />
          <b>{d.month}</b><span className="text-xs text-muted-foreground">{d.trigger === "manual" ? "Sent now" : "Scheduled"} · {fmtDateTime(d.at)} ET · {d.actor}</span>
        </div>
        {d.results.map((r) => (
          <div key={r.recipient} className="mt-1 text-xs text-muted-foreground">{r.recipient} — {r.status}{r.error && <span className="text-destructive"> · {r.error}</span>}</div>
        ))}
      </li>
    ))}
  </ul>
);

export const ReportSettings = () => {
  const [f, setF] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [log, setLog] = useState([]);
  const requested = useRef(false);
  const loadLog = useCallback(() => api.get("/reports/monthly-email/log").then((r) => setLog(r.data.items)), []);
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    api.get("/settings").then((r) => setF({ report_recipients: r.data.report_recipients.join(", "), report_enabled: r.data.report_enabled }));
    loadLog();
  }, [loadLog]);
  if (!f) return null;

  const save = async (e) => {
    e.preventDefault();
    const list = f.report_recipients.split(",").map((s) => s.trim()).filter(Boolean);
    const bad = list.filter((s) => !EMAIL_RE.test(s));
    if (!list.length) return setErr("Enter at least one email address");
    if (bad.length) return setErr(`Not a valid email address: ${bad.join(", ")}`);
    setBusy("save"); setErr("");
    try {
      const { data } = await api.put("/settings", { report_recipients: list, report_enabled: f.report_enabled });
      setF({ ...f, report_recipients: data.report_recipients.join(", ") });
      toast.success("Monthly report settings saved");
    } catch (ex) {
      const { message, fields } = apiError(ex);
      setErr(fields.report_recipients || message);
    } finally {
      setBusy("");
    }
  };
  const sendNow = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/reports/monthly-email/send-now");
      (data.status === "Sent" ? toast.success : toast.error)(`Report for ${data.month}: ${data.status}`);
      loadLog();
    } catch (ex) {
      toast.error(apiError(ex).message);
    } finally {
      setBusy("");
    }
  };

  return (
    <form onSubmit={save} noValidate className="fade-up mb-10 rounded-lg border bg-card p-6 sm:p-8" data-testid="report-settings-form">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Monthly report email</h3>
          <p className="mt-1 text-sm text-muted-foreground">On the 1st of each month (US/Eastern) the portfolio report for the month just ended is emailed as Excel.</p>
        </div>
        <Button type="button" variant="outline" onClick={sendNow} disabled={!!busy} data-testid="send-report-now-button">
          {busy === "send" ? <Loader2 className="animate-spin" /> : <Send />} Send report now
        </Button>
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-[1fr_auto]">
        <Field label="Monthly report recipients" name="report_recipients" error={err} hint="Comma-separated email addresses">
          <Input id="report_recipients" value={f.report_recipients} onChange={(e) => { setF({ ...f, report_recipients: e.target.value }); setErr(""); }}
            className={`h-10 bg-card ${err ? "border-destructive" : ""}`} data-testid="settings-report-recipients-input" />
        </Field>
        <label className="flex items-center gap-3 self-end rounded-md border px-4 py-2.5 text-sm font-semibold">
          <Switch checked={f.report_enabled} onCheckedChange={(v) => setF({ ...f, report_enabled: v })} data-testid="settings-report-enabled-switch" /> Send monthly
        </label>
      </div>
      <div className="mt-6 flex justify-end">
        <Button type="submit" disabled={!!busy} data-testid="report-settings-save-button">{busy === "save" && <Loader2 className="animate-spin" />} Save report settings</Button>
      </div>
      <ReportLog items={log} />
    </form>
  );
};
